import type { Table } from '@tanstack/react-table'
import { type ReactNode, useState } from 'react'

import { AddFilterButton, Filters, Search, TableExport } from '@/components'
import {
  DataTable,
  type DataTableColumns,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import type { Transaction } from '@/modules/projects/stores/types'
import { transactionFilterSchema } from '@/schemas/transactionFilterSchema'
import {
  FiltersProvider,
  useFilters,
  useSearchQuery,
  useTableFilters,
} from '@/stores'
import { transactionsColumns } from '../tables/transactions/columns'
import { TransactionsBulkActions } from '../tables/transactions/TransactionsBulkActions'

interface TransactionsDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description: ReactNode
  transactions: Transaction[]
  metadata?: ReactNode
  hasShareNumber?: boolean
  emptyMessage?: string
  contextId?: string
  exportFilename?: string
}

export function TransactionsDetailModal({
  contextId = 'transactions-detail-modal',
  ...props
}: TransactionsDetailModalProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="flex h-[90vh] w-[calc(100%-2rem)] max-w-[min(1400px,calc(100vw-2rem))] flex-col gap-3 overflow-hidden p-6"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <FiltersProvider contextId={contextId}>
          <TransactionsDetailModalContent {...props} contextId={contextId} />
        </FiltersProvider>
      </DialogContent>
    </Dialog>
  )
}

function TransactionsDetailModalContent({
  title,
  description,
  transactions,
  metadata,
  hasShareNumber = false,
  emptyMessage,
  contextId,
  exportFilename = 'transactions',
}: Omit<TransactionsDetailModalProps, 'open' | 'onOpenChange'> & {
  contextId: string
}) {
  const [table, setTable] = useState<Table<any> | null>(null)
  const filters = useFilters()
  const searchQuery = useSearchQuery()

  const schema = hasShareNumber
    ? transactionFilterSchema
    : transactionFilterSchema.filter((field) => field.field !== 'shareNumber')

  const { getUniqueValues } = useTableFilters<Transaction>({ table, schema })

  const columns = transactionsColumns(hasShareNumber) as DataTableColumns

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {metadata}

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border"
        style={{ contain: 'layout paint', transform: 'translateZ(0)' }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-blue-3 bg-blue-1/40 px-4 py-2.5">
          <Filters
            dialogId={contextId}
            schema={schema}
            getUniqueValues={getUniqueValues}
          />
          <div className="flex items-center gap-2">
            <Search />
            <AddFilterButton dialogId={contextId} schema={schema} />
            {table && <TableExport table={table} filename={exportFilename} />}
          </div>
        </div>
        <DataTable
          columns={columns}
          data={transactions}
          height="flex-1 min-h-0"
          emptyMessage={emptyMessage}
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
          onTableReady={setTable}
          filters={filters}
          filterSchema={schema}
          globalFilter={searchQuery}
          bulkActions={(selection) => (
            <TransactionsBulkActions
              selection={selection}
              transactions={transactions}
            />
          )}
        />
      </div>
    </>
  )
}
